import React, { useState, useEffect } from 'react';

const StudentGrievance = ({ onNavigate, userInfo }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [showSubmitForm, setShowSubmitForm] = useState(false);
    const [grievances, setGrievances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileError, setFileError] = useState('');
    const [userData, setUserData] = useState(null);
    const [formData, setFormData] = useState({
        category: '',
        subject: '',
        description: '',
        priority: 'medium'
    });

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in/api';

    const categories = [
        { id: 'food', label: 'Food & Mess', icon: '🍽️' },
        { id: 'room', label: 'Room Issues', icon: '🏠' },
        { id: 'maintenance', label: 'Maintenance', icon: '🔧' },
        { id: 'cleanliness', label: 'Cleanliness', icon: '🧹' },
        { id: 'security', label: 'Security', icon: '🛡️' },
        { id: 'facilities', label: 'Facilities', icon: '🏢' },
        { id: 'staff', label: 'Staff Behavior', icon: '👥' },
        { id: 'other', label: 'Other', icon: '📋' }
    ];

    const priorities = [
        { value: 'low', label: 'Low', color: '#4caf50' },
        { value: 'medium', label: 'Medium', color: '#ff9800' },
        { value: 'high', label: 'High', color: '#f44336' }
    ];

    // Fetch user data from backend
    const fetchUserData = async () => {
        try {
            const userId = userInfo?.userId || userInfo?.id;
            if (!userId) return;

            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const headers = {
                'Content-Type': 'application/json',
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE_URL}/student/dashboard/${userId}`, {
                method: 'GET',
                headers
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setUserData(data.dashboard);
                }
            }
        } catch (err) {
            console.error('Error fetching user data:', err);
        }
    };

    // Fetch grievances from backend
    const fetchGrievances = async () => {
        try {
            setLoading(true);
            setError(null);

            const userId = userInfo?.userId || userInfo?.id;
            if (!userId) {
                throw new Error('User ID not found');
            }

            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const headers = {
                'Content-Type': 'application/json',
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE_URL}/student/grievances/${userId}`, {
                method: 'GET',
                headers
            });

            if (!response.ok) {
                throw new Error('Failed to fetch grievances');
            }

            const data = await response.json();

            if (data.success) {
                setGrievances(data.grievances || []);
            } else {
                throw new Error(data.message || 'Failed to load grievances');
            }
        } catch (err) {
            console.error('Error fetching grievances:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Fetch both user data and grievances on mount
    useEffect(() => {
        fetchGrievances();
        fetchUserData();
    }, [userInfo]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFileError('');

        if (file) {
            const maxSize = 3 * 1024 * 1024;
            if (file.size > maxSize) {
                setFileError('File size must be less than 3MB');
                setSelectedFile(null);
                e.target.value = '';
                return;
            }

            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
            if (!allowedTypes.includes(file.type)) {
                setFileError('Only images (JPEG, JPG, PNG, GIF) and PDF files are allowed');
                setSelectedFile(null);
                e.target.value = '';
                return;
            }

            setSelectedFile(file);
        }
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setFileError('');
        const fileInput = document.getElementById('grievance-file-input');
        if (fileInput) fileInput.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.category || !formData.subject || !formData.description) {
            alert('Please fill in all required fields');
            return;
        }

        if (fileError) {
            alert('Please fix the file error before submitting');
            return;
        }

        try {
            setSubmitting(true);
            const userId = userInfo?.userId || userInfo?.id;

            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            
            const formDataToSend = new FormData();
            formDataToSend.append('category', formData.category);
            formDataToSend.append('subject', formData.subject);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('priority', formData.priority);
            
            if (selectedFile) {
                formDataToSend.append('file', selectedFile);
            }

            const headers = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE_URL}/student/grievances/${userId}`, {
                method: 'POST',
                headers,
                body: formDataToSend
            });

            const data = await response.json();

            if (data.success) {
                setSubmitSuccess(true);
                setFormData({
                    category: '',
                    subject: '',
                    description: '',
                    priority: 'medium'
                });
                setSelectedFile(null);
                setFileError('');
                
                const fileInput = document.getElementById('grievance-file-input');
                if (fileInput) fileInput.value = '';
                
                setTimeout(() => {
                    setShowSubmitForm(false);
                    setSubmitSuccess(false);
                    fetchGrievances();
                }, 2000);
            } else {
                throw new Error(data.message || 'Failed to submit grievance');
            }
        } catch (err) {
            console.error('Error submitting grievance:', err);
            alert(err.message || 'Failed to submit grievance');
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return '#ff9800';
            case 'in-progress': return '#2196f3';
            case 'resolved': return '#4caf50';
            case 'rejected': return '#f44336';
            default: return '#757575';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending': return '⏳';
            case 'in-progress': return '🔄';
            case 'resolved': return '✅';
            case 'rejected': return '❌';
            default: return '📋';
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const MenuItem = ({ icon, label, active = false, route }) => (
        <div
            className={`menu-item ${active ? 'menu-item-active' : ''}`}
            onClick={() => {
                if (route && onNavigate) {
                    onNavigate(route);
                }
                setSidebarOpen(false);
            }}
        >
            <span className="menu-icon">{icon}</span>
            <span className="menu-label">{label}</span>
            <div className="menu-glow"></div>
        </div>
    );

    const handleLogout = () => {
        if (onNavigate) {
            onNavigate('hostel-login');
        }
    };

    // Get display name with fallbacks
    const displayName = userData?.name || userInfo?.name || userInfo?.fullName || userInfo?.username || 'Student';
    const displayInitial = displayName.charAt(0).toUpperCase();

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#ffffff',
                flexDirection: 'column',
                gap: '20px'
            }}>
                <div style={{
                    width: '60px',
                    height: '60px',
                    border: '6px solid #f3f3f3',
                    borderTop: '6px solid #2b6777',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{ color: '#2b6777', fontSize: '18px', fontWeight: '600' }}>
                    Loading Grievances...
                </p>
            </div>
        );
    }

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                body {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    -webkit-font-smoothing: antialiased;
                    overflow-x: hidden;
                }

                @keyframes slideIn {
                    from { opacity: 0; transform: translateY(30px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                @keyframes shimmer {
                    0% { background-position: -1000px 0; }
                    100% { background-position: 1000px 0; }
                }

                @keyframes pulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                }

                @keyframes glow {
                    0%, 100% { opacity: 0.5; }
                    50% { opacity: 1; }
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .dashboard-container {
                    display: flex;
                    height: 100vh;
                    background: #ffffff;
                    position: relative;
                    overflow: hidden;
                }

                .sidebar {
                    position: fixed;
                    top: 0;
                    left: 0;
                    bottom: 0;
                    width: 300px;
                    background: linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(242,242,242,0.98) 100%);
                    backdrop-filter: blur(30px);
                    border-right: 2px solid rgba(82,171,152,0.2);
                    transform: translateX(-100%);
                    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    z-index: 100;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 8px 0 40px rgba(43,103,119,0.15);
                }

                .sidebar.open {
                    transform: translateX(0);
                }

                .sidebar-header {
                    padding: 36px 28px;
                    background: linear-gradient(135deg, rgba(43,103,119,0.05) 0%, rgba(82,171,152,0.05) 100%);
                    border-bottom: 2px solid rgba(82,171,152,0.15);
                    position: relative;
                    overflow: hidden;
                }

                .sidebar-header::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(82,171,152,0.15), transparent);
                    animation: shimmer 4s infinite;
                }

                .sidebar-top {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    margin-bottom: 24px;
                    position: relative;
                    z-index: 1;
                }

                .close-btn {
                    background: rgba(43,103,119,0.08);
                    border: 1.5px solid rgba(82,171,152,0.25);
                    cursor: pointer;
                    font-size: 20px;
                    color: #2b6777;
                    width: 38px;
                    height: 38px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                }

                .close-btn:hover {
                    background: rgba(43,103,119,0.15);
                    border-color: #52ab98;
                    transform: rotate(90deg);
                }

                .user-info-sidebar {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    position: relative;
                    z-index: 1;
                }

                .user-avatar-sidebar {
                    width: 56px;
                    height: 56px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 800;
                    font-size: 22px;
                    color: white;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                    border: 3px solid rgba(255, 255, 255, 0.5);
                }

                .user-details-sidebar {
                    flex: 1;
                }

                .user-name-sidebar {
                    font-size: 16px;
                    font-weight: 800;
                    color: #2b6777;
                    margin-bottom: 4px;
                }

                .user-role-sidebar {
                    font-size: 13px;
                    color: #52ab98;
                    font-weight: 600;
                }

                .sidebar-nav {
                    flex: 1;
                    padding: 28px 20px;
                    overflow-y: auto;
                }

                .sidebar-nav::-webkit-scrollbar {
                    width: 6px;
                }

                .sidebar-nav::-webkit-scrollbar-track {
                    background: rgba(82,171,152,0.08);
                }

                .sidebar-nav::-webkit-scrollbar-thumb {
                    background: rgba(82,171,152,0.3);
                    border-radius: 3px;
                }

                .menu-item {
                    position: relative;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    padding: 16px 18px;
                    margin-bottom: 10px;
                    cursor: pointer;
                    color: #2b6777;
                    font-size: 15px;
                    font-weight: 600;
                    border-radius: 14px;
                    transition: all 0.3s;
                    overflow: hidden;
                }

                .menu-glow {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
                    opacity: 0;
                    transition: opacity 0.3s;
                }

                .menu-item:hover {
                    color: #52ab98;
                    background: rgba(82,171,152,0.1);
                    transform: translateX(6px);
                    box-shadow: 0 4px 16px rgba(82,171,152,0.15);
                }

                .menu-item:hover .menu-glow {
                    opacity: 1;
                }

                .menu-item-active {
                    background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
                    color: #2b6777;
                    box-shadow: 0 6px 20px rgba(82,171,152,0.2);
                    font-weight: 700;
                }

                .menu-item-active .menu-glow {
                    opacity: 1;
                    animation: glow 2s ease-in-out infinite;
                }

                .menu-icon {
                    font-size: 22px;
                    position: relative;
                    z-index: 1;
                }

                .menu-label {
                    position: relative;
                    z-index: 1;
                }

                .logout-section {
                    padding: 20px;
                    border-top: 2px solid rgba(82,171,152,0.15);
                }

                .logout-btn {
                    width: 100%;
                    padding: 16px 20px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    color: white;
                    border: none;
                    border-radius: 14px;
                    font-size: 15px;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    transition: all 0.3s;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                }

                .logout-btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 10px 35px rgba(43,103,119,0.4);
                }

                .overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(43,103,119,0.4);
                    backdrop-filter: blur(6px);
                    z-index: 90;
                    opacity: 0;
                    visibility: hidden;
                    transition: all 0.3s ease;
                }

                .overlay.show {
                    opacity: 1;
                    visibility: visible;
                }

                .main-content {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    width: 100%;
                    position: relative;
                    z-index: 1;
                }

                .top-header {
                    background: #ffffff;
                    padding: 24px 20px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid rgba(82,171,152,0.12);
                    position: relative;
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                }

                .top-header::after {
                    content: '';
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
                }

                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    flex: 1;
                }

                .menu-toggle {
                    background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
                    border: 2px solid rgba(82,171,152,0.25);
                    cursor: pointer;
                    font-size: 24px;
                    color: #2b6777;
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                    box-shadow: 0 4px 20px rgba(43,103,119,0.15);
                    flex-shrink: 0;
                }

                .menu-toggle:hover {
                    transform: scale(1.05);
                    box-shadow: 0 6px 30px rgba(43,103,119,0.25);
                    background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
                }

                .header-title-section {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    min-width: 0;
                    flex: 1;
                }

                .header-greeting {
                    font-size: 13px;
                    color: #52ab98;
                    font-weight: 600;
                    display: none;
                }

                .header-name {
                    font-size: 18px;
                    font-weight: 900;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .header-right {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-shrink: 0;
                }

                .user-avatar-header {
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 800;
                    font-size: 18px;
                    color: white;
                    cursor: pointer;
                    border: 3px solid rgba(82,171,152,0.3);
                    transition: all 0.3s;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                }

                .user-avatar-header:hover {
                    transform: scale(1.1);
                    box-shadow: 0 8px 32px rgba(43,103,119,0.5);
                }

                .content-wrapper {
                    flex: 1;
                    overflow-y: auto;
                    padding: 20px 16px;
                }

                .content-wrapper::-webkit-scrollbar {
                    width: 8px;
                }

                .content-wrapper::-webkit-scrollbar-track {
                    background: rgba(82,171,152,0.08);
                }

                .content-wrapper::-webkit-scrollbar-thumb {
                    background: rgba(82,171,152,0.3);
                    border-radius: 4px;
                }

                .content-area {
                    max-width: 1500px;
                    margin: 0 auto;
                    animation: slideIn 0.6s ease-out;
                }

                .hero-section {
                    background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
                    border-radius: 12px;
                    padding: 28px 20px;
                    margin-bottom: 24px;
                    border: 1px solid rgba(82,171,152,0.15);
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                    position: relative;
                    overflow: hidden;
                }

                .hero-section::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
                }

                .hero-content {
                    position: relative;
                    z-index: 1;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                }

                .hero-text {
                    flex: 1;
                }

                .hero-title {
                    font-size: 24px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 8px;
                    letter-spacing: -0.5px;
                }

                .hero-subtitle {
                    font-size: 13px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 500;
                    line-height: 1.4;
                }

                .submit-btn {
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    color: white;
                    border: none;
                    border-radius: 12px;
                    padding: 14px 24px;
                    font-size: 14px;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.3s;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                    flex-shrink: 0;
                }

                .submit-btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 10px 35px rgba(43,103,119,0.4);
                }

                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px;
                    margin-bottom: 24px;
                }

                .stat-card {
                    background: #ffffff;
                    border-radius: 12px;
                    padding: 20px 16px;
                    border: 1px solid rgba(43,103,119,0.12);
                    cursor: pointer;
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                }

                .stat-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 4px;
                    height: 100%;
                    background: linear-gradient(180deg, #2b6777 0%, #52ab98 100%);
                    transform: scaleY(0);
                    transition: transform 0.3s ease;
                }

                .stat-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 24px rgba(43,103,119,0.12);
                    border-color: rgba(82,171,152,0.25);
                }

                .stat-card:hover::before {
                    transform: scaleY(1);
                }

                .stat-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    margin-bottom: 16px;
                }

                .stat-label {
                    font-size: 10px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .stat-icon {
                    width: 36px;
                    height: 36px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    background: rgba(82,171,152,0.08);
                    border: 1px solid rgba(82,171,152,0.15);
                }

                .stat-value {
                    font-size: 26px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 6px;
                    letter-spacing: -0.5px;
                    line-height: 1;
                }

                .stat-subtext {
                    font-size: 11px;
                    color: rgba(43,103,119,0.5);
                    font-weight: 500;
                }

                .form-modal {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(43,103,119,0.5);
                    backdrop-filter: blur(8px);
                    z-index: 200;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    animation: fadeIn 0.3s ease;
                }

                .form-container {
                    background: white;
                    border-radius: 20px;
                    padding: 32px 24px;
                    max-width: 600px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 24px 70px rgba(43,103,119,0.25);
                    animation: slideUp 0.4s ease;
                    border: 2px solid rgba(82,171,152,0.2);
                }

                .form-container::-webkit-scrollbar {
                    width: 6px;
                }

                .form-container::-webkit-scrollbar-track {
                    background: rgba(82,171,152,0.08);
                }

                .form-container::-webkit-scrollbar-thumb {
                    background: rgba(82,171,152,0.3);
                    border-radius: 3px;
                }

                .form-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 28px;
                    padding-bottom: 20px;
                    border-bottom: 2px solid rgba(82,171,152,0.15);
                }

                .form-title {
                    font-size: 22px;
                    font-weight: 900;
                    color: #2b6777;
                }

                .close-form-btn {
                    background: rgba(43,103,119,0.08);
                    border: 1.5px solid rgba(82,171,152,0.25);
                    cursor: pointer;
                    font-size: 20px;
                    color: #2b6777;
                    width: 38px;
                    height: 38px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                }

                .close-form-btn:hover {
                    background: rgba(43,103,119,0.15);
                    border-color: #52ab98;
                    transform: rotate(90deg);
                }

                .category-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px;
                    margin-bottom: 24px;
                }

                .category-option {
                    padding: 16px;
                    border: 2px solid rgba(82,171,152,0.2);
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.3s;
                    text-align: center;
                    background: rgba(255,255,255,0.9);
                }

                .category-option:hover {
                    border-color: rgba(82,171,152,0.4);
                    transform: translateY(-2px);
                    box-shadow: 0 4px 16px rgba(43,103,119,0.12);
                }

                .category-option.selected {
                    background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
                    border-color: #52ab98;
                }

                .category-icon {
                    font-size: 28px;
                    margin-bottom: 8px;
                }

                .category-label {
                    font-size: 13px;
                    font-weight: 700;
                    color: #2b6777;
                }

                .form-group {
                    margin-bottom: 24px;
                }

                .form-label {
                    display: block;
                    font-size: 13px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 10px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .required {
                    color: #f44336;
                }

                .form-input,
                .form-textarea {
                    width: 100%;
                    padding: 14px 16px;
                    border: 2px solid rgba(82,171,152,0.2);
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #2b6777;
                    transition: all 0.3s;
                    font-family: 'Inter', sans-serif;
                }

                .form-input:focus,
                .form-textarea:focus {
                    outline: none;
                    border-color: #52ab98;
                    box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
                }

                .form-textarea {
                    min-height: 120px;
                    resize: vertical;
                }

                .file-upload-area {
                    border: 2px dashed rgba(82,171,152,0.3);
                    border-radius: 12px;
                    padding: 24px;
                    text-align: center;
                    transition: all 0.3s;
                    background: rgba(82,171,152,0.03);
                    cursor: pointer;
                }

                .file-upload-area:hover {
                    border-color: #52ab98;
                    background: rgba(82,171,152,0.08);
                }

                .file-upload-area.has-file {
                    border-color: #52ab98;
                    background: rgba(82,171,152,0.1);
                }

                .file-upload-icon {
                    font-size: 48px;
                    margin-bottom: 12px;
                }

                .file-upload-text {
                    font-size: 14px;
                    font-weight: 600;
                    color: #2b6777;
                    margin-bottom: 8px;
                }

                .file-upload-hint {
                    font-size: 12px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 500;
                }

                .file-upload-input {
                    display: none;
                }

                .file-preview {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    padding: 12px 16px;
                    background: rgba(82,171,152,0.1);
                    border-radius: 10px;
                    margin-top: 12px;
                }

                .file-info {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex: 1;
                    min-width: 0;
                }

                .file-icon-preview {
                    font-size: 32px;
                    flex-shrink: 0;
                }

                .file-details {
                    flex: 1;
                    min-width: 0;
                }

                .file-name {
                    font-size: 13px;
                    font-weight: 700;
                    color: #2b6777;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    margin-bottom: 4px;
                }

                .file-size {
                    font-size: 11px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 600;
                }

                .remove-file-btn {
                    background: rgba(244,67,54,0.1);
                    border: 1.5px solid rgba(244,67,54,0.3);
                    color: #f44336;
                    cursor: pointer;
                    font-size: 16px;
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                    flex-shrink: 0;
                }

                .remove-file-btn:hover {
                    background: rgba(244,67,54,0.2);
                    border-color: #f44336;
                    transform: scale(1.1);
                }

                .file-error {
                    color: #f44336;
                    font-size: 12px;
                    font-weight: 600;
                    margin-top: 8px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                .priority-options {
                    display: flex;
                    gap: 12px;
                }

                .priority-option {
                    flex: 1;
                    padding: 14px;
                    border: 2px solid rgba(82,171,152,0.2);
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.3s;
                    text-align: center;
                    font-weight: 700;
                    font-size: 13px;
                }

                .priority-option:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 16px rgba(43,103,119,0.12);
                }

                .priority-option.selected {
                    border-width: 3px;
                }

                .form-actions {
                    display: flex;
                    gap: 12px;
                    margin-top: 32px;
                }

                .cancel-btn {
                    flex: 1;
                    padding: 16px;
                    background: rgba(82,171,152,0.1);
                    border: 2px solid rgba(82,171,152,0.25);
                    border-radius: 12px;
                    font-size: 15px;
                    font-weight: 700;
                    color: #2b6777;
                    cursor: pointer;
                    transition: all 0.3s;
                }

                .cancel-btn:hover {
                    background: rgba(82,171,152,0.2);
                    transform: translateY(-2px);
                }

                .submit-form-btn {
                    flex: 2;
                    padding: 16px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    border: none;
                    border-radius: 12px;
                    font-size: 15px;
                    font-weight: 700;
                    color: white;
                    cursor: pointer;
                    transition: all 0.3s;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                }

                .submit-form-btn:hover:not(:disabled) {
                    transform: translateY(-3px);
                    box-shadow: 0 10px 35px rgba(43,103,119,0.4);
                }

                .submit-form-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .success-message {
                    background: linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%);
                    border: 2px solid rgba(76, 175, 80, 0.3);
                    border-radius: 12px;
                    padding: 16px;
                    text-align: center;
                    margin-bottom: 20px;
                    animation: slideUp 0.3s ease;
                }

                .success-icon {
                    font-size: 48px;
                    margin-bottom: 12px;
                }

                .success-text {
                    font-size: 16px;
                    font-weight: 700;
                    color: #4caf50;
                }

                .grievances-list {
                    display: grid;
                    gap: 16px;
                }

                .grievance-card {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 24px 20px;
                    border: 2px solid rgba(82,171,152,0.2);
                    transition: all 0.3s;
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                    position: relative;
                    overflow: hidden;
                }

                .grievance-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 5px;
                    height: 100%;
                    background: var(--status-color);
                }

                .grievance-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 24px rgba(43,103,119,0.12);
                    border-color: rgba(82,171,152,0.3);
                }

                .grievance-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 12px;
                    margin-bottom: 16px;
                }

                .grievance-left {
                    flex: 1;
                    min-width: 0;
                }

                .grievance-category {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(82,171,152,0.1);
                    border: 1.5px solid rgba(82,171,152,0.2);
                    border-radius: 8px;
                    padding: 6px 12px;
                    font-size: 11px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 10px;
                }

                .grievance-subject {
                    font-size: 18px;
                    font-weight: 900;
                    color: #2b6777;
                    margin-bottom: 8px;
                    line-height: 1.3;
                }

                .grievance-description {
                    font-size: 13px;
                    color: rgba(43,103,119,0.7);
                    line-height: 1.5;
                    margin-bottom: 12px;
                }

                .grievance-attachment {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(82,171,152,0.08);
                    border: 1.5px solid rgba(82,171,152,0.2);
                    border-radius: 8px;
                    padding: 8px 12px;
                    font-size: 11px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 12px;
                }

                .grievance-status {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 8px 14px;
                    border-radius: 10px;
                    font-size: 12px; 
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    background-color: var(--status-bg);
                    color: var(--status-color);
                    border: 2px solid var(--status-color);
                }

                .grievance-footer {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    padding-top: 16px;
                    border-top: 2px solid rgba(82,171,152,0.1);
                }

                .grievance-date {
                    font-size: 12px;
                    color: rgba(43,103,119,0.5);
                    font-weight: 600;
                }

                .grievance-id {
                    font-size: 11px;
                    color: rgba(43,103,119,0.4);
                    font-weight: 700;
                }

                .empty-state {
                    text-align: center;
                    padding: 80px 20px;
                    color: rgba(43,103,119,0.5);
                }

                .empty-state-icon {
                    font-size: 80px;
                    margin-bottom: 24px;
                    opacity: 0.3;
                }

                .empty-state-title {
                    font-size: 24px;
                    font-weight: 800;
                    color: #2b6777;
                    margin-bottom: 12px;
                }

                .empty-state-text {
                    font-size: 16px;
                    font-weight: 600;
                    color: rgba(43,103,119,0.6);
                    margin-bottom: 32px;
                }

                .empty-state-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 16px 32px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    color: white;
                    border: none;
                    border-radius: 12px;
                    font-size: 15px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.3s;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                }

                .empty-state-btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 10px 35px rgba(43,103,119,0.4);
                }

                @media (min-width: 768px) {
                    .top-header {
                        padding: 32px 24px;
                    }

                    .header-name {
                        font-size: 22px;
                    }

                    .header-greeting {
                        display: block;
                        font-size: 14px;
                    }

                    .header-title-section {
                        flex-direction: row;
                        align-items: center;
                        gap: 12px;
                    }

                    .content-wrapper {
                        padding: 32px 24px;
                    }

                    .hero-section {
                        padding: 48px 28px;
                    }

                    .hero-title {
                        font-size: 32px;
                    }

                    .hero-subtitle {
                        font-size: 15px;
                    }

                    .stats-grid {
                        grid-template-columns: repeat(4, 1fr);
                        gap: 16px;
                    }

                    .stat-card {
                        padding: 24px 20px;
                    }

                    .category-grid {
                        grid-template-columns: repeat(4, 1fr);
                    }

                    .grievances-list {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 20px;
                    }
                }

                @media (min-width: 1024px) {
                    .sidebar {
                        position: static;
                        transform: translateX(0);
                    }

                    .close-btn {
                        display: none;
                    }

                    .overlay {
                        display: none !important;
                    }

                    .menu-toggle {
                        display: none;
                    }

                    .top-header {
                        padding: 52px 32px;
                    }

                    .header-name {
                        font-size: 28px;
                    }

                    .header-greeting {
                        font-size: 18px;
                    }

                    .content-wrapper {
                        padding: 56px 48px;
                    }

                    .hero-section {
                        padding: 60px 36px;
                    }

                    .hero-title {
                        font-size: 36px;
                    }

                    .hero-subtitle {
                        font-size: 16px;
                    }

                    .stats-grid {
                        gap: 20px;
                    }

                    .stat-card {
                        padding: 28px;
                    }

                    .grievances-list {
                        gap: 24px;
                    }

                    .grievance-card {
                        padding: 32px 28px;
                    }
                }

                @media (max-width: 380px) {
                    .header-name {
                        font-size: 16px;
                    }

                    .hero-title {
                        font-size: 20px;
                    }

                    .submit-btn {
                        padding: 12px 16px;
                        font-size: 13px;
                    }
                }
            `}</style>

            <div className="dashboard-container">
                {/* Sidebar */}
                <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
                    <div className="sidebar-header">
                        <div className="sidebar-top">
                            <button onClick={() => setSidebarOpen(false)} className="close-btn">
                                ✕
                            </button>
                        </div>
                        <div className="user-info-sidebar">
                            <div className="user-avatar-sidebar">
                                {displayInitial}
                            </div>
                            <div className="user-details-sidebar">
                                <div className="user-name-sidebar">
                                    {displayName}
                                </div>
                                <div className="user-role-sidebar">Student Portal</div>
                            </div>
                        </div>
                    </div>

                    <nav className="sidebar-nav">
                        <MenuItem icon="🏠" label="Dashboard" route="hostel-student-dashboard" />
                        <MenuItem icon="🚪" label="Entry/Exit Log" route="hostel-student-entry-exit-log" />
                        <MenuItem icon="🛏" label="Room Details" route="hostel-student-room-details" />
                        <MenuItem icon="🍽️" label="Food Preference" route="hostel-student-food-preference" />
                        <MenuItem icon="🧺" label="Laundry Service" route="hostel-student-laundry" />
                        <MenuItem icon="💳" label="Grievance" active={true} route="hostel-student-payments" />
                        <MenuItem icon="📝" label="Requests" route="hostel-student-requests" />
                        <MenuItem icon="📋" label="Requisition" route="hostel-student-requisition" />
                    </nav>

                    <div className="logout-section">
                        <button onClick={handleLogout} className="logout-btn">
                            🚪 Logout
                        </button>
                    </div>
                </div>

                <div className={`overlay ${sidebarOpen ? 'show' : ''}`} onClick={() => setSidebarOpen(false)} />

                {/* Main Content */}
                <div className="main-content">
                    <div className="top-header">
                        <div className="header-left">
                            <button className="menu-toggle" onClick={() => setSidebarOpen(true)}>☰</button>
                            <div className="header-title-section">
                                <div className="header-greeting">Welcome back,</div>
                                <div className="header-name">
                                    {displayName}
                                </div>
                            </div>
                        </div>
                        <div className="header-right">
                            <div className="user-avatar-header">
                                {displayInitial}
                            </div>
                        </div>
                    </div>

                    <div className="content-wrapper">
                        <div className="content-area">
                            {/* Hero Section */}
                            <div className="hero-section">
                                <div className="hero-content">
                                    <div className="hero-text">
                                        <h1 className="hero-title">Grievance Portal 📢</h1>
                                        <p className="hero-subtitle">
                                            Submit your concerns and track their resolution status
                                        </p>
                                    </div>
                                    <button className="submit-btn" onClick={() => setShowSubmitForm(true)}>
                                        ➕ New Grievance
                                    </button>
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="stats-grid">
                                <div className="stat-card">
                                    <div className="stat-header">
                                        <div className="stat-label">Total</div>
                                        <div className="stat-icon">📊</div>
                                    </div>
                                    <div className="stat-value">{grievances.length}</div>
                                    <div className="stat-subtext">All grievances</div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-header">
                                        <div className="stat-label">Pending</div>
                                        <div className="stat-icon">⏳</div>
                                    </div>
                                    <div className="stat-value">
                                        {grievances.filter(g => g.status === 'pending').length}
                                    </div>
                                    <div className="stat-subtext">Awaiting review</div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-header">
                                        <div className="stat-label">In Progress</div>
                                        <div className="stat-icon">🔄</div>
                                    </div>
                                    <div className="stat-value">
                                        {grievances.filter(g => g.status === 'in-progress').length}
                                    </div>
                                    <div className="stat-subtext">Being resolved</div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-header">
                                        <div className="stat-label">Resolved</div>
                                        <div className="stat-icon">✅</div>
                                    </div>
                                    <div className="stat-value">
                                        {grievances.filter(g => g.status === 'resolved').length}
                                    </div>
                                    <div className="stat-subtext">Completed</div>
                                </div>
                            </div>

                            {/* Grievances List */}
                            {grievances.length > 0 ? (
                                <div className="grievances-list">
                                    {grievances.map((grievance) => (
                                        <div 
                                            key={grievance.id} 
                                            className="grievance-card"
                                            style={{
                                                '--status-color': getStatusColor(grievance.status),
                                                '--status-bg': `${getStatusColor(grievance.status)}15`
                                            }}
                                        >
                                            <div className="grievance-header">
                                                <div className="grievance-left">
                                                    <div className="grievance-category">
                                                        {categories.find(c => c.id === grievance.category)?.icon}
                                                        <span>{categories.find(c => c.id === grievance.category)?.label}</span>
                                                    </div>
                                                    <h3 className="grievance-subject">{grievance.subject}</h3>
                                                    <p className="grievance-description">{grievance.description}</p>
                                                    {grievance.fileName && (
                                                        <div className="grievance-attachment">
                                                            📎 {grievance.fileName}
                                                        </div>
                                                    )}
                                                </div>
                                                <div 
                                                    className="grievance-status"
                                                    style={{
                                                        '--status-color': getStatusColor(grievance.status),
                                                        '--status-bg': `${getStatusColor(grievance.status)}15`
                                                    }}
                                                >
                                                    {getStatusIcon(grievance.status)}
                                                    <span>{grievance.status.replace('-', ' ')}</span>
                                                </div>
                                            </div>
                                            <div className="grievance-footer">
                                                <div className="grievance-date">
                                                    Submitted: {new Date(grievance.createdAt).toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric'
                                                    })}
                                                </div>
                                                <div className="grievance-id">#{grievance.grievanceNumber}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <div className="empty-state-icon">📢</div>
                                    <h2 className="empty-state-title">No Grievances Yet</h2>
                                    <p className="empty-state-text">
                                        Submit your first grievance to get started
                                    </p>
                                    <button className="empty-state-btn" onClick={() => setShowSubmitForm(true)}>
                                        ➕ Submit Grievance
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Submit Form Modal */}
                {showSubmitForm && (
                    <div className="form-modal" onClick={(e) => {
                        if (e.target.className === 'form-modal') setShowSubmitForm(false);
                    }}>
                        <div className="form-container">
                            <div className="form-header">
                                <h2 className="form-title">Submit New Grievance</h2>
                                <button 
                                    className="close-form-btn" 
                                    onClick={() => setShowSubmitForm(false)}
                                >
                                    ✕
                                </button>
                            </div>

                            {submitSuccess && (
                                <div className="success-message">
                                    <div className="success-icon">✅</div>
                                    <div className="success-text">Grievance submitted successfully!</div>
                                </div>
                            )}

                            <form onSubmit={handleSubmit}>
                                <div className="form-group">
                                    <label className="form-label">
                                        Category <span className="required">*</span>
                                    </label>
                                    <div className="category-grid">
                                        {categories.map((cat) => (
                                            <div
                                                key={cat.id}
                                                className={`category-option ${formData.category === cat.id ? 'selected' : ''}`}
                                                onClick={() => setFormData({...formData, category: cat.id})}
                                            >
                                                <div className="category-icon">{cat.icon}</div>
                                                <div className="category-label">{cat.label}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Subject <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="Brief summary of your concern"
                                        value={formData.subject}
                                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Description <span className="required">*</span>
                                    </label>
                                    <textarea
                                        className="form-textarea"
                                        placeholder="Provide detailed information about your grievance..."
                                        value={formData.description}
                                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Attachment (Optional)
                                    </label>
                                    <div 
                                        className={`file-upload-area ${selectedFile ? 'has-file' : ''}`}
                                        onClick={() => document.getElementById('grievance-file-input').click()}
                                    >
                                        <div className="file-upload-icon">
                                            {selectedFile ? '📎' : '📁'}
                                        </div>
                                        <div className="file-upload-text">
                                            {selectedFile ? 'File Selected' : 'Click to upload file'}
                                        </div>
                                        <div className="file-upload-hint">
                                            Images (JPEG, PNG, GIF) or PDF • Max 3MB
                                        </div>
                                    </div>
                                    <input
                                        type="file"
                                        id="grievance-file-input"
                                        className="file-upload-input"
                                        accept="image/jpeg,image/jpg,image/png,image/gif,application/pdf"
                                        onChange={handleFileChange}
                                    />
                                    {selectedFile && (
                                        <div className="file-preview">
                                            <div className="file-info">
                                                <div className="file-icon-preview">
                                                    {selectedFile.type.startsWith('image/') ? '🖼️' : '📄'}
                                                </div>
                                                <div className="file-details">
                                                    <div className="file-name">{selectedFile.name}</div>
                                                    <div className="file-size">{formatFileSize(selectedFile.size)}</div>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="remove-file-btn"
                                                onClick={handleRemoveFile}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    )}
                                    {fileError && (
                                        <div className="file-error">
                                            ⚠️ {fileError}
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Priority Level</label>
                                    <div className="priority-options">
                                        {priorities.map((priority) => (
                                            <div
                                                key={priority.value}
                                                className={`priority-option ${formData.priority === priority.value ? 'selected' : ''}`}
                                                style={{
                                                    borderColor: formData.priority === priority.value ? priority.color : 'rgba(82,171,152,0.2)',
                                                    color: formData.priority === priority.value ? priority.color : '#2b6777'
                                                }}
                                                onClick={() => setFormData({...formData, priority: priority.value})}
                                            >
                                                {priority.label}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="form-actions">
                                    <button
                                        type="button"
                                        className="cancel-btn"
                                        onClick={() => setShowSubmitForm(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="submit-form-btn"
                                        disabled={submitting}
                                    >
                                        {submitting ? 'Submitting...' : 'Submit Grievance'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default StudentGrievance;